import { audio, mtof, rand } from './engine.js';

/**
 * A generative score. Each piece picks a key, mode and chord progression to suit the time
 * of day and the region, plays through intro / theme / variation / outro, then leaves
 * a stretch of silence so the ambience can breathe (the way older MMO soundtracks do).
 * Notes are scheduled a little ahead of time on the audio clock, so timing never drifts.
 */
export interface Mood { night: number; region: string }

const MODES: Record<string, number[]> = {
  ionian: [0, 2, 4, 5, 7, 9, 11], lydian: [0, 2, 4, 6, 7, 9, 11], mixolydian: [0, 2, 4, 5, 7, 9, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10], aeolian: [0, 2, 3, 5, 7, 8, 10], phrygian: [0, 1, 3, 5, 7, 8, 10],
};
const PROGRESSIONS = {
  bright: [[0, 4, 5, 3], [0, 3, 0, 4], [5, 3, 0, 4], [0, 5, 3, 4], [3, 4, 0, 0]],
  dark: [[0, 5, 3, 4], [0, 3, 5, 4], [0, 6, 5, 6], [0, 0, 5, 6]],
  mystic: [[0, 1, 0, 4], [0, 3, 1, 0], [0, 4, 1, 5]],
};
type Section = 'intro' | 'theme' | 'variation' | 'outro';
interface Piece { root: number; mode: number[]; prog: number[][]; bpm: number; plan: Section[]; flavour: 'bright' | 'dark' | 'mystic'; lead: 'pluck' | 'flute' | 'bell'; seed: number }

export class Music {
  private piece: Piece | null = null;
  private bar = 0;
  private nextBarAt = 0;
  private restUntil = 0;
  private timer = 0;
  private prevNote = 0;
  mood: Mood = { night: 0, region: '' };

  constructor() { audio.onStart(() => { this.restUntil = audio.now + 4; this.timer = window.setInterval(() => this.schedule(), 120); }); }

  private compose(): Piece {
    const { night, region } = this.mood;
    const dark = night > 0.6 || region === 'fen' || region === 'ashen';
    const mystic = !dark && (region === 'grove' || region === 'cove' || (night > 0.3 && Math.random() < 0.5));
    const flavour = dark ? 'dark' : mystic ? 'mystic' : 'bright';
    const modeName = flavour === 'dark' ? (region === 'ashen' ? 'phrygian' : Math.random() < 0.5 ? 'dorian' : 'aeolian') : flavour === 'mystic' ? 'lydian' : (['ionian', 'lydian', 'mixolydian'] as const)[Math.floor(Math.random() * 3)];
    const prog = PROGRESSIONS[flavour][Math.floor(Math.random() * PROGRESSIONS[flavour].length)];
    const root = [50, 52, 53, 55, 57][Math.floor(Math.random() * 5)];
    return {
      root, mode: MODES[modeName], prog: [prog], bpm: flavour === 'dark' ? rand(56, 64) : rand(66, 78), flavour, seed: Math.random(),
      plan: ['intro', 'intro', 'theme', 'theme', 'variation', 'variation', 'theme', 'outro'],
      lead: flavour === 'mystic' ? 'bell' : night > 0.5 || Math.random() < 0.3 ? 'flute' : 'pluck',
    };
  }

  private degree(p: Piece, d: number, octave = 0) { const n = p.mode.length; const o = Math.floor(d / n); return p.root + p.mode[((d % n) + n) % n] + 12 * (o + octave); }

  private schedule() {
    const ctx = audio.ctx;
    if (!ctx || audio.settings.music <= 0.001) return;
    const now = ctx.currentTime;
    if (!this.piece) {
      if (now < this.restUntil) return;
      this.piece = this.compose(); this.bar = 0; this.nextBarAt = now + 0.2;
    }
    const p = this.piece, barLen = (60 / p.bpm) * 4;
    while (this.nextBarAt < now + 0.6) {
      const phraseBars = 4, section = p.plan[Math.floor(this.bar / phraseBars)];
      if (!section) { this.piece = null; this.restUntil = now + rand(25, 55); return; }
      this.playBar(p, section, this.bar % phraseBars, this.nextBarAt, barLen);
      this.bar++; this.nextBarAt += barLen;
    }
  }

  private playBar(p: Piece, section: Section, i: number, t: number, barLen: number) {
    const chordDeg = p.prog[0][i % p.prog[0].length];
    const chord = [0, 2, 4].map(k => this.degree(p, chordDeg + k));
    const beat = barLen / 4, at = (x: number) => Math.max(0, t - audio.now + x);
    // pad: always, softer in the intro and outro
    const padGain = section === 'intro' || section === 'outro' ? 0.035 : 0.05;
    for (const m of chord) this.pad(mtof(m), at(0), barLen * 1.15, padGain);
    if (section !== 'intro') this.bass(mtof(this.degree(p, chordDeg, -1)), at(0), barLen * 0.9);
    if (section === 'theme' || (section === 'outro' && i === 0)) this.melody(p, chord, t, beat, 1);
    if (section === 'variation') { this.arp(chord, t, beat); if (i % 2 === 1) this.melody(p, chord, t, beat, 0.6); }
  }

  private melody(p: Piece, chord: number[], t: number, beat: number, density: number) {
    const rhythms = [[0, 1.5, 2, 3], [0, 2, 3.5], [0, 0.5, 1, 2, 3], [0, 3], [1, 2, 2.5, 3], [0, 1, 2]];
    const rhythm = rhythms[Math.floor(Math.random() * rhythms.length)].filter(() => Math.random() < density + 0.2);
    for (const pos of rhythm) {
      const strong = pos % 2 === 0;
      let note: number;
      if (strong || !this.prevNote) note = chord[Math.floor(Math.random() * 3)] + 12;
      else { const steps = [-2, -1, 1, 2][Math.floor(Math.random() * 4)]; const idx = p.mode.findIndex(s => (this.prevNote - p.root - s) % 12 === 0); note = idx >= 0 ? this.degree(p, idx + steps + 7 * Math.floor((this.prevNote - p.root) / 12)) : chord[1] + 12; }
      note = Math.max(p.root + 7, Math.min(p.root + 26, note));
      this.prevNote = note;
      const when = Math.max(0, t - audio.now + pos * beat), len = beat * (strong ? 1.6 : 0.9);
      if (p.lead === 'flute') this.flute(mtof(note), when, len);
      else if (p.lead === 'bell') this.bellLead(mtof(note), when);
      else this.pluck(mtof(note), when, 0.07);
    }
  }
  private arp(chord: number[], t: number, beat: number) {
    const notes = [...chord, chord[0] + 12, chord[1] + 12, ...chord.slice().reverse()];
    notes.slice(0, 8).forEach((m, k) => this.pluck(mtof(m + 12), Math.max(0, t - audio.now + k * beat * 0.5), 0.035));
  }

  /* ---- instruments ---- */
  private pad(f: number, at: number, dur: number, gain: number) {
    const ctx = audio.ctx!, t = ctx.currentTime + at;
    const out = audio.out({ bus: 'music', gain, reverb: 0.6 }, at + dur + 1);
    if (!out) return;
    const filter = ctx.createBiquadFilter(); filter.type = 'lowpass'; filter.frequency.value = 900; filter.Q.value = 0.5;
    const env = ctx.createGain(); env.gain.setValueAtTime(0.0001, t); env.gain.linearRampToValueAtTime(1, t + Math.min(1.8, dur * 0.4)); env.gain.setValueAtTime(1, t + dur * 0.7); env.gain.linearRampToValueAtTime(0.0001, t + dur + 0.8);
    filter.connect(env); env.connect(out);
    for (const d of [-7, 6]) { const o = ctx.createOscillator(); o.type = 'sawtooth'; o.frequency.value = f; o.detune.value = d; o.connect(filter); o.start(t); o.stop(t + dur + 1); }
  }
  private bass(f: number, at: number, dur: number) { audio.tone({ bus: 'music', type: 'triangle', freq: f, dur, gain: 0.09, at, attack: 0.04 }); audio.tone({ bus: 'music', freq: f / 2, dur, gain: 0.06, at, attack: 0.04 }); }
  private pluck(f: number, at: number, gain: number) { audio.tone({ bus: 'music', freq: f, dur: 1.4, gain, at, reverb: 0.45 }); audio.tone({ bus: 'music', type: 'triangle', freq: f * 2.003, dur: 0.35, gain: gain * 0.35, at, reverb: 0.45 }); }
  private bellLead(f: number, at: number) { audio.bell(f, [[1, 0.06], [2.76, 0.025], [5.4, 0.01]], 2.6, { bus: 'music', at, reverb: 0.7 }); }
  private flute(f: number, at: number, dur: number) {
    const ctx = audio.ctx!, t = ctx.currentTime + at;
    const out = audio.out({ bus: 'music', gain: 0.055, reverb: 0.55 }, at + dur + 0.5);
    if (!out) return;
    const o = ctx.createOscillator(); o.type = 'sine'; o.frequency.value = f;
    const vib = ctx.createOscillator(), vibG = ctx.createGain(); vib.frequency.value = 5; vibG.gain.setValueAtTime(0, t); vibG.gain.linearRampToValueAtTime(f * 0.006, t + 0.4);
    vib.connect(vibG); vibG.connect(o.frequency);
    const env = ctx.createGain(); env.gain.setValueAtTime(0.0001, t); env.gain.linearRampToValueAtTime(1, t + 0.12); env.gain.setValueAtTime(1, t + dur * 0.8); env.gain.linearRampToValueAtTime(0.0001, t + dur + 0.25);
    o.connect(env); env.connect(out);
    o.start(t); vib.start(t); o.stop(t + dur + 0.3); vib.stop(t + dur + 0.3);
    audio.noiseHit({ bus: 'music', gain: 0.012, filter: 'bandpass', freq: f * 2, q: 2, dur: dur * 0.6, at, attack: 0.08 });
  }
  stop() { clearInterval(this.timer); }
}
